import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { markIntroComplete, getSessionConfig } from '../../services/api';
import LanternMark from '../../components/LanternMark';

const C = {
  bg: '#FFFDF7',
  text: '#1A1F36',
  textMuted: '#5A6478',
  accent: '#F59E0B',
  accentDark: '#D97706',
  accentSoft: '#FEF3C7',
  border: '#F0E7D5',
  card: '#FFFFFF',
};

const HEADING_FONT = "'Nunito', sans-serif";

export default function ChildIntro({ onDone }) {
  const navigate = useNavigate();

  const [displayName, setDisplayName] = useState(null);
  const [step, setStep] = useState(0);
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);

  // Pull the child's name from session-config so the greeting feels personal.
  // Falls back to a friendly generic if the call fails for any non-auth reason.
  useEffect(() => {
    let cancelled = false;
    getSessionConfig()
      .then((r) => {
        if (cancelled) return;
        setDisplayName(r.data?.displayName || 'there');
      })
      .catch((err) => {
        if (cancelled) return;
        if (err.response?.status === 401) {
          navigate('/login');
          return;
        }
        setDisplayName('there');
      });
    return () => { cancelled = true; };
  }, [navigate]);

  const messages = displayName
    ? [
        `Hi ${displayName}! I'm Lantern, your learning assistant. I'm here to help you think through problems — not just give you answers! That means I'll ask you questions to help you figure things out yourself. Pretty cool, right?`,
        `A couple of things to know: everything we talk about is private between you and your parent. They can see our conversations, which means you're always safe. And if you ever need help, there's an "I need help" button at the top of the screen.`,
        `Ready to get started? Pick a subject and let's go!`,
      ]
    : [];

  async function handleDone() {
    setLoading(true);
    try {
      await markIntroComplete();
    } catch {}
    setLoading(false);
    setDone(true);
    setTimeout(() => {
      if (onDone) {
        onDone();
      } else {
        navigate('/child');
      }
    }, 1200);
  }

  // Loading state — wait for the child's name before showing personalised copy.
  // The bobbing LanternMark is already animated by its own glow keyframe.
  if (!displayName) {
    return (
      <div style={{
        minHeight: '100vh',
        background: `${C.bg} radial-gradient(ellipse 600px 400px at 50% -10%, rgba(245,158,11,0.11) 0%, rgba(245,158,11,0) 60%)`,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        fontFamily: "'Source Sans 3', sans-serif",
      }}>
        <LanternMark size={64} />
      </div>
    );
  }

  return (
    <>
      <style>{`
        @keyframes ci-pop { 0%{opacity:0;transform:translateY(8px) scale(0.98);} 100%{opacity:1;transform:translateY(0) scale(1);} }
        @keyframes ci-msg-in { 0%{opacity:0;transform:translateY(6px);} 100%{opacity:1;transform:translateY(0);} }
        @keyframes ci-bob { 0%,100%{transform:translateY(0);} 50%{transform:translateY(-4px);} }
        @keyframes ci-spin { to { transform: rotate(360deg); } }
        @keyframes ci-tail-pop { 0%{opacity:0;transform:scale(0.6);} 100%{opacity:1;transform:scale(1);} }
        @keyframes ci-celebrate { 0%{transform:scale(0.6);opacity:0;} 60%{transform:scale(1.15);opacity:1;} 100%{transform:scale(1);opacity:1;} }
        @keyframes ci-confetti { 0%{transform:translate(-50%,0) rotate(0deg);opacity:1;} 100%{transform:translate(var(--tx,0),var(--ty,-120px)) rotate(var(--rot,360deg));opacity:0;} }
        .ci-cta {
          min-width: 180px;
          min-height: 56px;
          padding: 16px 32px;
          color: #fff;
          border: none;
          border-radius: 18px;
          font-family: ${HEADING_FONT};
          font-size: 18px;
          font-weight: 800;
          letter-spacing: 0.3px;
          cursor: pointer;
          transition: transform 90ms ease, background 160ms ease, box-shadow 160ms ease;
          display: inline-flex;
          align-items: center;
          justify-content: center;
          gap: 8px;
          -webkit-tap-highlight-color: transparent;
        }
        .ci-cta--primary {
          background: ${C.accent};
          box-shadow: 0 4px 0 ${C.accentDark}, 0 8px 20px ${C.accent}55;
        }
        .ci-cta--primary:hover:not(:disabled) {
          background: ${C.accentDark};
          box-shadow: 0 4px 0 ${C.accentDark}, 0 10px 24px ${C.accent}66;
        }
        .ci-cta--primary:active:not(:disabled) {
          transform: translateY(3px);
          box-shadow: 0 1px 0 ${C.accentDark}, 0 4px 10px ${C.accent}55;
        }
        .ci-cta--final {
          background: linear-gradient(135deg, ${C.accent} 0%, #FBBF24 100%);
          box-shadow: 0 4px 0 ${C.accentDark}, 0 12px 28px ${C.accent}66;
          padding: 18px 36px;
          font-size: 19px;
        }
        .ci-cta--final:hover:not(:disabled) {
          box-shadow: 0 4px 0 ${C.accentDark}, 0 14px 32px ${C.accent}88;
        }
        .ci-cta--final:active:not(:disabled) {
          transform: translateY(3px);
          box-shadow: 0 1px 0 ${C.accentDark}, 0 6px 14px ${C.accent}66;
        }
        .ci-cta:disabled {
          opacity: 0.6;
          cursor: not-allowed;
        }
        .ci-back {
          min-height: 56px;
          padding: 14px 22px;
          background: transparent;
          color: ${C.textMuted};
          border: 2px solid ${C.border};
          border-radius: 18px;
          font-family: ${HEADING_FONT};
          font-size: 16px;
          font-weight: 700;
          cursor: pointer;
          transition: border-color 160ms ease, color 160ms ease, background 160ms ease;
          -webkit-tap-highlight-color: transparent;
        }
        .ci-back:hover { border-color: ${C.accent}; color: ${C.text}; background: ${C.accentSoft}; }
        .ci-spin {
          width: 18px; height: 18px; border-radius: 50%;
          border: 2.5px solid rgba(255,255,255,0.4);
          border-top-color: #fff;
          animation: ci-spin 700ms linear infinite;
        }
        .ci-card { animation: ci-pop 320ms cubic-bezier(0.2,0.8,0.2,1) both; }
        .ci-msg { animation: ci-msg-in 320ms cubic-bezier(0.2,0.8,0.2,1) both; }
        .ci-avatar { animation: ci-bob 3s ease-in-out infinite; }
        .ci-confetti-piece {
          position: absolute;
          left: 50%; top: 50%;
          width: 8px; height: 12px; border-radius: 2px;
          animation: ci-confetti 1100ms cubic-bezier(0.2,0.6,0.4,1) forwards;
        }
      `}</style>

      <div style={{
        minHeight: '100vh',
        background: `${C.bg} radial-gradient(ellipse 600px 400px at 50% -10%, rgba(245,158,11,0.11) 0%, rgba(245,158,11,0) 60%)`,
        // env(safe-area-inset-top) keeps the intro card clear of the iOS
        // status bar / camera notch in standalone PWA mode.
        padding: 'calc(32px + env(safe-area-inset-top)) 18px 40px',
        fontFamily: "'Source Sans 3', sans-serif",
        color: C.text,
        boxSizing: 'border-box',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
      }}>
        {/* Brand */}
        <div className="ci-card" style={{ textAlign: 'center', marginBottom: 20 }}>
          <div className="ci-avatar" style={{ display: 'flex', justifyContent: 'center', marginBottom: 8 }}>
            <LanternMark size={56} />
          </div>
          <h1 style={{
            margin: 0,
            fontFamily: HEADING_FONT,
            fontSize: 22,
            fontWeight: 800,
            color: C.text,
            letterSpacing: '-0.5px',
          }}>
            Lantern
          </h1>
        </div>

        {/* Speech bubble card */}
        <div className="ci-card" style={{
          width: '100%',
          maxWidth: 460,
          position: 'relative',
        }}>
          {/* Avatar above bubble */}
          <div style={{
            display: 'flex',
            alignItems: 'flex-end',
            gap: 12,
            marginBottom: 8,
            paddingLeft: 4,
          }}>
            <div className="ci-avatar" aria-hidden="true" style={{ flexShrink: 0 }}>
              <LanternMark size={44} />
            </div>
            <div style={{
              fontFamily: HEADING_FONT,
              fontSize: 13,
              fontWeight: 700,
              color: C.accentDark,
              paddingBottom: 4,
              letterSpacing: '0.04em',
            }}>
              LANTERN says…
            </div>
          </div>

          <div style={{
            background: C.card,
            border: `2px solid ${C.border}`,
            borderRadius: 24,
            padding: '24px 22px',
            boxShadow: '0 10px 30px rgba(26,31,54,0.08), 0 2px 6px rgba(26,31,54,0.04)',
            position: 'relative',
            minHeight: 200,
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'space-between',
          }}>
            {/* Speech tail */}
            <div aria-hidden="true" style={{
              position: 'absolute',
              top: -10,
              left: 32,
              width: 20,
              height: 20,
              background: C.card,
              border: `2px solid ${C.border}`,
              borderRight: 'none',
              borderBottom: 'none',
              transform: 'rotate(45deg)',
              borderTopLeftRadius: 4,
              animation: 'ci-tail-pop 260ms ease both',
            }} />

            {/* Message */}
            <p
              key={step}
              className="ci-msg"
              style={{
                fontSize: 16,
                color: C.text,
                lineHeight: 1.6,
                margin: 0,
                fontFamily: "'Source Sans 3', sans-serif",
                textWrap: 'pretty',
              }}
            >
              {messages[step]}
            </p>

            {/* Step dots */}
            <div style={{
              display: 'flex',
              gap: 10,
              justifyContent: 'center',
              marginTop: 24,
              alignItems: 'center',
            }}>
              {messages.map((_, i) => {
                const active = i === step;
                const past = i < step;
                return (
                  <button
                    key={i}
                    onClick={() => setStep(i)}
                    aria-label={`Go to message ${i + 1}`}
                    aria-current={active ? 'step' : undefined}
                    style={{
                      width: active ? 28 : 10,
                      height: 10,
                      borderRadius: 5,
                      background: active ? C.accent : (past ? C.accent + '99' : C.border),
                      border: 'none',
                      padding: 0,
                      cursor: 'pointer',
                      transition: 'width 220ms ease, background 220ms ease',
                      boxShadow: active ? `0 2px 6px ${C.accent}66` : 'none',
                    }}
                  />
                );
              })}
            </div>
          </div>
        </div>

        {/* Action buttons */}
        <div className="ci-card" style={{
          marginTop: 24,
          display: 'flex',
          gap: 12,
          alignItems: 'center',
        }}>
          {step > 0 && step < messages.length - 1 && (
            <button
              type="button"
              className="ci-back"
              onClick={() => setStep((s) => s - 1)}
            >
              Back
            </button>
          )}
          {step < messages.length - 1 ? (
            <button
              type="button"
              className="ci-cta ci-cta--primary"
              onClick={() => setStep((s) => s + 1)}
            >
              <span>Next</span>
              <svg width="16" height="16" viewBox="0 0 16 16" fill="none" aria-hidden="true">
                <path d="M3 8h10M9 4l4 4-4 4" stroke="#fff" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round" />
              </svg>
            </button>
          ) : (
            <button
              type="button"
              className="ci-cta ci-cta--final"
              onClick={handleDone}
              disabled={loading || done}
            >
              {loading ? (
                <>
                  <span className="ci-spin" aria-hidden="true" />
                  <span>Getting ready…</span>
                </>
              ) : (
                <>
                  <svg width="20" height="20" viewBox="0 0 20 20" fill="none" aria-hidden="true">
                    <path d="M10 1.5 L12.4 7.6 L19 8.2 L14 12.6 L15.6 19 L10 15.6 L4.4 19 L6 12.6 L1 8.2 L7.6 7.6 Z"
                      fill="#fff" />
                  </svg>
                  <span>Let's go!</span>
                </>
              )}
            </button>
          )}
        </div>

        {/* Step counter */}
        <p style={{
          marginTop: 16,
          fontSize: 13,
          color: C.textMuted,
          fontFamily: HEADING_FONT,
          fontWeight: 600,
        }}>
          {step + 1} of {messages.length}
        </p>

        {/* Done overlay */}
        {done && (
          <div role="status" aria-live="polite" style={{
            position: 'fixed',
            inset: 0,
            background: `${C.bg}f2`,
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            animation: 'ci-pop 200ms ease both',
            zIndex: 50,
            pointerEvents: 'none',
          }}>
            <div style={{ position: 'relative', marginBottom: 16, animation: 'ci-celebrate 600ms cubic-bezier(0.2,0.8,0.2,1) both' }}>
              <LanternMark size={96} />
              {[...Array(12)].map((_, i) => {
                const angle = (i / 12) * Math.PI * 2;
                const dist = 80 + Math.random() * 30;
                const colors = [C.accent, '#FBBF24', '#10B981', C.text, '#FCD34D'];
                return (
                  <div
                    key={i}
                    className="ci-confetti-piece"
                    style={{
                      background: colors[i % colors.length],
                      animationDelay: `${i * 30}ms`,
                      '--tx': `${Math.cos(angle) * dist}px`,
                      '--ty': `${Math.sin(angle) * dist - 20}px`,
                      '--rot': `${360 + Math.random() * 360}deg`,
                    }}
                  />
                );
              })}
            </div>
            <div style={{
              fontFamily: HEADING_FONT,
              fontSize: 24,
              fontWeight: 800,
              color: C.text,
            }}>
              Let's learn!
            </div>
          </div>
        )}
      </div>
    </>
  );
}
